import { useEffect, useState } from "react";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Link } from "react-router-dom";
import { ArrowUpRight, PlayCircle, StopCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, PieChart, Pie, Cell } from "recharts";

const GOLD_PALETTE = ["#C9A961", "#8C6F1E", "#D4B87D", "#A8853A", "#F0E1B0", "#5C4813"];

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="border border-neutral-200 bg-white p-6" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500">{label}</div>
      <div className="mt-3 font-display font-black text-4xl tracking-tighter leading-none">{value}</div>
      {sub && <div className="mt-2 text-xs text-neutral-500 font-mono">{sub}</div>}
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [todayAtt, setTodayAtt] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
    } catch (e) {
      toast.error("Failed to load stats");
    }
    if (user.role === "company_admin" || user.role === "super_admin") {
      try { const { data } = await api.get("/analytics/overview"); setAnalytics(data); }
      catch (e) { console.error("analytics/overview failed", e); }
    }
  };

  const loadToday = async () => {
    try {
      const { data } = await api.get("/attendance/today");
      setTodayAtt(data);
    } catch (e) { console.error("attendance/today failed", e); }
  };

  useEffect(() => {
    load();
    if (user.role === "employee") loadToday();
    // eslint-disable-next-line
  }, []);

  const checkIn = async () => {
    try {
      await api.post("/attendance/checkin");
      toast.success("Checked in");
      loadToday();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Check-in failed"));
    }
  };
  const checkOut = async () => {
    try {
      await api.post("/attendance/checkout");
      toast.success("Checked out");
      loadToday();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Check-out failed"));
    }
  };

  if (!stats) {
    return <div className="p-10 font-mono text-xs text-neutral-500 uppercase tracking-widest">Loading…</div>;
  }

  return (
    <div>
      <PageHeader
        testid="page-overview"
        eyebrow={`Signed in · ${user.role.replace("_", " ")}`}
        title={
          user.role === "super_admin" ? "Firm HQ." :
          user.role === "company_admin" ? "Payroll ops." :
          "Your day."
        }
        description={
          user.role === "super_admin"
            ? "Every client company on your books, at one glance."
            : user.role === "company_admin"
            ? "Attendance, employees and the next payroll run — all right here."
            : "Clock in, check payslips, request leave."
        }
      />

      <div className="p-8 space-y-8">
        {stats.role === "super_admin" && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi label="Client companies" value={stats.companies} testid="kpi-companies" />
              <Kpi label="Total employees" value={stats.employees} testid="kpi-employees" />
              <Kpi label="Payroll runs" value={stats.payroll_runs} testid="kpi-runs" />
              <Kpi label="Total disbursed" value={fmtMoney(stats.total_paid)} sub="Across all clients (net)" testid="kpi-paid" />
            </div>
            <div className="border border-neutral-200 bg-white p-8 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#002FA7]">Action</div>
                <div className="font-display font-bold text-2xl tracking-tighter mt-1">Onboard a new client company</div>
                <p className="text-sm text-neutral-600 mt-1">Provision a workspace and issue an HR admin login in seconds.</p>
              </div>
              <Link to="/app/companies" data-testid="cta-manage-companies" className="inline-flex items-center gap-2 bg-neutral-950 text-white px-5 py-3 font-mono text-sm hover:bg-[#002FA7] transition-colors">
                Manage clients <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </>
        )}

        {stats.role === "company_admin" && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi label="Active employees" value={stats.employees} testid="kpi-employees" />
              <Kpi label="Present today" value={stats.present_today} testid="kpi-present" />
              <Kpi label="Pending leaves" value={stats.pending_leaves} testid="kpi-pending-leaves" />
              <Kpi label="Monthly wage bill" value={fmtMoney(stats.monthly_wage_bill)} sub="Sum of net (active)" testid="kpi-wage-bill" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-neutral-200 bg-white p-6">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#002FA7]">Last payroll run</div>
                {stats.last_payroll_run ? (
                  <div className="mt-3">
                    <div className="font-display font-bold text-2xl tracking-tighter">
                      {String(stats.last_payroll_run.month).padStart(2, "0")}/{stats.last_payroll_run.year}
                    </div>
                    <div className="text-sm text-neutral-600 mt-1 font-mono">
                      {stats.last_payroll_run.employee_count} employees · {fmtMoney(stats.last_payroll_run.total_net)} net
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-sm text-neutral-500">No payroll run yet. Start one from the Payroll tab.</div>
                )}
                <Link to="/app/payroll" data-testid="cta-payroll" className="mt-6 inline-flex items-center gap-2 border border-neutral-950 px-4 py-2 font-mono text-sm hover:bg-neutral-950 hover:text-white transition-colors">
                  Run next cycle <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="border border-neutral-200 bg-neutral-950 text-white p-6">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF3B30]">Add employees</div>
                <div className="font-display font-bold text-2xl tracking-tighter mt-3">Grow the roster.</div>
                <p className="text-sm text-neutral-400 mt-1">Each new employee gets a login, salary structure and payslip access.</p>
                <Link to="/app/employees" data-testid="cta-employees" className="mt-6 inline-flex items-center gap-2 bg-white text-neutral-950 px-4 py-2 font-mono text-sm hover:bg-[#002FA7] hover:text-white transition-colors">
                  Manage employees <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {analytics && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-strip">
                  <Kpi label="Attendance MTD" value={`${analytics.attendance_rate_mtd || 0}%`} testid="kpi-att-rate" />
                  <Kpi label="Open tickets" value={analytics.open_tickets || 0} testid="kpi-open-tickets" />
                  <Kpi label="Pending expenses" value={analytics.pending_expenses || 0} testid="kpi-pending-expenses" />
                  <Kpi label="Pending leaves" value={analytics.pending_leaves || 0} testid="kpi-pending-leaves-2" />
                </div>
                <div className="grid lg:grid-cols-2 gap-4" data-testid="analytics-charts">
                  <div className="border border-neutral-200 bg-white p-6">
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#8C6F1E] mb-4">Payroll trend · last 12 runs</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={analytics.payroll_trend || []} margin={{top: 10, right: 20, left: 0, bottom: 0}}>
                        <XAxis dataKey="period" tick={{fontSize: 10, fontFamily: "monospace", fill: "#8C6F1E"}} />
                        <YAxis tick={{fontSize: 10, fontFamily: "monospace", fill: "#8C6F1E"}} tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} />
                        <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{fontFamily: "monospace", fontSize: 12, border: "1px solid #C9A961"}} />
                        <Line type="monotone" dataKey="net" stroke="#C9A961" strokeWidth={2} dot={{fill: "#8C6F1E"}} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="border border-neutral-200 bg-white p-6">
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#8C6F1E] mb-4">Headcount by department</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={analytics.headcount_by_dept || []} dataKey="count" nameKey="dept" cx="50%" cy="50%" outerRadius={80} label={{fontSize: 10, fontFamily: "monospace"}}>
                          {(analytics.headcount_by_dept || []).map((d, i) => <Cell key={d.dept || `slice-${i}`} fill={GOLD_PALETTE[i % GOLD_PALETTE.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{fontFamily: "monospace", fontSize: 12, border: "1px solid #C9A961"}} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {stats.role === "employee" && (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-neutral-200 bg-white p-8">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#002FA7]">Attendance · today</div>
                <div className="mt-3 font-display font-black text-4xl tracking-tighter">
                  {todayAtt?.check_in ? new Date(todayAtt.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                  <span className="text-neutral-400 text-2xl mx-2">→</span>
                  {todayAtt?.check_out ? new Date(todayAtt.check_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                </div>
                <div className="mt-2 text-sm text-neutral-500 font-mono flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> {todayAtt?.hours || 0} hours logged
                </div>
                <div className="mt-6 flex gap-2">
                  <button onClick={checkIn} data-testid="btn-checkin" disabled={!!todayAtt?.check_in}
                    className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2.5 font-mono text-sm hover:bg-neutral-950 transition-colors disabled:opacity-40">
                    <PlayCircle className="w-4 h-4" /> Check in
                  </button>
                  <button onClick={checkOut} data-testid="btn-checkout" disabled={!todayAtt?.check_in || !!todayAtt?.check_out}
                    className="inline-flex items-center gap-2 border border-neutral-950 px-4 py-2.5 font-mono text-sm hover:bg-neutral-950 hover:text-white transition-colors disabled:opacity-40">
                    <StopCircle className="w-4 h-4" /> Check out
                  </button>
                </div>
              </div>

              <div className="border border-neutral-200 bg-neutral-950 text-white p-8">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF3B30]">Earnings · YTD</div>
                <div className="mt-3 font-display font-black text-5xl tracking-tighter">{fmtMoney(stats.ytd_earnings)}</div>
                <div className="mt-2 text-sm text-neutral-400 font-mono">
                  {stats.last_payslip
                    ? `Last: ${String(stats.last_payslip.month).padStart(2, "0")}/${stats.last_payslip.year} · ${fmtMoney(stats.last_payslip.net)}`
                    : "No payslip yet"}
                </div>
                <Link to="/app/payslips" data-testid="cta-payslips" className="mt-6 inline-flex items-center gap-2 bg-white text-neutral-950 px-4 py-2.5 font-mono text-sm hover:bg-[#002FA7] hover:text-white transition-colors">
                  View payslips <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            {stats.employee && (
              <div className="border border-neutral-200 bg-white p-6 grid md:grid-cols-4 gap-6">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Code</div>
                  <div className="mt-1 font-mono text-sm">{stats.employee.employee_code}</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Department</div>
                  <div className="mt-1 font-mono text-sm">{stats.employee.department}</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Designation</div>
                  <div className="mt-1 font-mono text-sm">{stats.employee.designation}</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Joined</div>
                  <div className="mt-1 font-mono text-sm">{stats.employee.joining_date}</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
